import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { Tooltip } from '../../Tooltip';
import { IS_MAC } from '../../../utils';

// Create a div to use as a panel for the CodeMirror editor
export const buildPanelElement = classSuffix => {
  let dom = document.createElement('div');
  if (classSuffix) dom.classList.add(`cm-${classSuffix}`);
  return dom;
};

const tooltipProps = {
  container: '.code-editor',
  className: 'tooltip-light',
  placement: 'top-end',
  modifiers: [
    { name: 'arrow', options: { padding: 8 } },
    { name: 'offset', options: { offset: [8, 4] } },
  ],
  delay: { show: 200 },
  strategy: 'fixed',
};

// Copies the provided value to the clipboard and shows a success tooltip
const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(null);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(copiedId => {
      if (!!copiedId) clearTimeout(copiedId);
      return setTimeout(() => {
        setCopied(null);
      }, 2000);
    });
  };

  return (
    value !== null && (
      <Tooltip
        content={!copied ? 'Copy to Clipboard' : 'Copied'}
        {...(!!copied ? { isOpen: true } : {})}
        {...tooltipProps}
      >
        {({ ref }) => (
          <button
            ref={ref}
            type="button"
            className="btn"
            onClick={copy}
            aria-label="Copy to Clipboard"
          >
            {!copied ? <Icon name="copy" sm /> : <Icon name="check" sm />}
          </button>
        )}
      </Tooltip>
    )
  );
};

/*
 * Renders our custom info panel with the language label and buttons for 
 * expanding, copying, and toggling the search and shortcuts panels.
 */
export const InfoPanel = ({
  language,
  languageSyntax,
  languageHelpText,
  expand,
  isExpanded,
  toggleSearch,
  toggleShortcuts,
  triggerAutocompletions,
  copyValue,
}) => {
  return (
    <>
      <div className="language">
        {language}
        {languageSyntax && (
          <Tooltip
            disabled={!languageHelpText}
            content={languageHelpText}
            {...tooltipProps}
            placement="top-start"
          >
            {({ ref }) => (
              <div ref={ref} className="badge">
                {languageSyntax}
              </div>
            )}
          </Tooltip>
        )}
      </div>

      <div className="actions">
        {!!triggerAutocompletions && (
          <Tooltip
            content={
              <div className="shortcut-item">
                <span>Show Suggestions</span>
                <span className="keys">
                  <span className="key">Ctrl</span>
                  <span className="key">Space</span>
                </span>
              </div>
            }
            {...tooltipProps}
          >
            {({ ref }) => (
              <button
                ref={ref}
                type="button"
                className="btn"
                onClick={triggerAutocompletions}
                aria-label="Show Suggestions"
              >
                <Icon name="code" sm />
              </button>
            )}
          </Tooltip>
        )}
        {!!toggleSearch && (
          <Tooltip
            content={
              <div className="shortcut-item">
                <span>Search</span>
                <span className="keys">
                  <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
                  <span className="key">F</span>
                </span>
              </div>
            }
            {...tooltipProps}
          >
            {({ ref }) => (
              <button
                ref={ref}
                type="button"
                className="btn"
                onClick={toggleSearch}
                aria-label="Toggle Search"
              >
                <Icon name="search" sm />
              </button>
            )}
          </Tooltip>
        )}
        {!!toggleShortcuts && (
          <Tooltip
            content={
              <div className="shortcut-item">
                <span>Keyboard Shortcuts</span>
                <span className="keys">
                  <span className="key">F1</span>
                </span>
              </div>
            }
            {...tooltipProps}
          >
            {({ ref }) => (
              <button
                ref={ref}
                type="button"
                className="btn"
                onClick={() => toggleShortcuts()}
                aria-label="Toggle Keyboard Shortcuts"
              >
                <Icon name="command" sm />
              </button>
            )}
          </Tooltip>
        )}
        <CopyButton value={copyValue} />
        {!isExpanded && (
          <Tooltip
            content={
              <div className="shortcut-item">
                <span>Maximize</span>
                <span className="keys">
                  <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
                  <span className="key">M</span>
                </span>
              </div>
            }
            {...tooltipProps}
          >
            {({ ref }) => (
              <button
                ref={ref}
                type="button"
                className="btn"
                onClick={expand}
                aria-label="Maximize Editor"
              >
                <Icon name="maximize-2" sm />
              </button>
            )}
          </Tooltip>
        )}
      </div>
    </>
  );
};

/*
 * Renders our custom shortcuts panel with a list of available keyboard 
 * shortcuts in the code editor.
 */
export const ShortcutsPanel = ({ toggle }) => {
  return (
    <>
      <div className="title">
        <span>Keyboard Shortcuts</span>
        <button
          type="button"
          className="btn-close"
          onClick={() => toggle(false)}
          aria-label="Close Shortcuts"
          title="Close Shortcuts"
        />
      </div>
      <div className="shortcuts">
        <div className="shortcut-item">
          <span>Suggestions:</span>
          <span className="keys">
            <span className="key">Ctrl</span>
            <span className="key">Space</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Find / Replace:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
            <span className="key">F</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Undo / Redo:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
            <span className="key">Z {'\u2215'} Y</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Go to Line:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2325' : 'Alt'}</span>
            <span className="key">G</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Move Line:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2325' : 'Alt'}</span>
            <span className="key">{'\u2191 \u2215 \u2193'}</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Copy Line:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2325' : 'Alt'}</span>
            <span className="key">Shift</span>
            <span className="key">{'\u2191 \u2215 \u2193'}</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Delete Line:</span>
          <span className="keys">
            <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
            <span className="key">Shift</span>
            <span className="key">K</span>
          </span>
        </div>
        <div className="shortcut-item">
          <span>Tab Out:</span>
          <span className="keys">
            <span className="key">Esc</span>
            {'\u21E8'}
            <span className="key">Tab</span>
          </span>
        </div>
      </div>
    </>
  );
};
